/**
 * useDailyPreJoin Hook
 *
 * Manages camera/mic preview and device state before joining a Daily.co call.
 * Uses native browser APIs for reliable preview, and tracks device state
 * to pass to the Daily.co join call.
 */

import { useState, useEffect, useCallback, useRef } from 'react';

export interface PreJoinDeviceState {
  audioEnabled: boolean;
  videoEnabled: boolean;
}

export interface PreJoinError {
  type: 'permission-denied' | 'no-camera' | 'no-microphone' | 'unknown';
  message: string;
}

export interface UseDailyPreJoinReturn {
  // The MediaStream for video preview
  stream: MediaStream | null;
  // Current device state
  audioEnabled: boolean;
  videoEnabled: boolean;
  // Loading/error states
  isInitializing: boolean;
  isReady: boolean;
  error: PreJoinError | null;
  // Device info
  hasCameraDevice: boolean;
  hasMicrophoneDevice: boolean;
  // Audio level for visual feedback (0-100)
  audioLevel: number;
  // Actions
  toggleAudio: () => void;
  toggleVideo: () => void;
  retry: () => void;
  // Cleanup function
  destroy: () => void;
}

export function useDailyPreJoin(): UseDailyPreJoinReturn {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<PreJoinError | null>(null);
  const [hasCameraDevice, setHasCameraDevice] = useState(true);
  const [hasMicrophoneDevice, setHasMicrophoneDevice] = useState(true);
  const [audioLevel, setAudioLevel] = useState(0);

  // Track if component is mounted
  const isMountedRef = useRef(true);
  // Track current stream for cleanup
  const streamRef = useRef<MediaStream | null>(null);
  // Audio analyzer refs
  const audioContextRef = useRef<AudioContext | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  // Prevent double initialization
  const initializingRef = useRef(false);

  // Initialize the camera/mic stream
  const initialize = useCallback(async () => {
    if (initializingRef.current || !isMountedRef.current) return;
    initializingRef.current = true;

    setIsInitializing(true);
    setError(null);
    setIsReady(false);

    console.log('[useDailyPreJoin] Starting initialization...');

    try {
      // First check what devices are available
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasCamera = devices.some(d => d.kind === 'videoinput');
      const hasMic = devices.some(d => d.kind === 'audioinput');

      console.log('[useDailyPreJoin] Devices found - Camera:', hasCamera, 'Mic:', hasMic);

      if (!isMountedRef.current) {
        initializingRef.current = false;
        return;
      }

      setHasCameraDevice(hasCamera);
      setHasMicrophoneDevice(hasMic);

      // Request media access
      const constraints: MediaStreamConstraints = {
        video: hasCamera ? true : false,
        audio: hasMic ? true : false,
      };

      // If no devices at all, we can't do much
      if (!hasCamera && !hasMic) {
        setError({
          type: 'unknown',
          message: 'No camera or microphone detected. Please connect a device.',
        });
        setIsInitializing(false);
        initializingRef.current = false;
        return;
      }

      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

      if (!isMountedRef.current) {
        mediaStream.getTracks().forEach(track => track.stop());
        initializingRef.current = false;
        return;
      }

      streamRef.current = mediaStream;
      setStream(mediaStream);

      // Set initial enabled states based on what we got
      const hasVideoTrack = mediaStream.getVideoTracks().length > 0;
      const hasAudioTrack = mediaStream.getAudioTracks().length > 0;

      if (!hasVideoTrack) {
        setHasCameraDevice(false);
        setVideoEnabled(false);
      }
      if (!hasAudioTrack) {
        setHasMicrophoneDevice(false);
        setAudioEnabled(false);
      }

      // Set up audio level monitoring
      if (hasAudioTrack) {
        try {
          audioContextRef.current = new AudioContext();
          const analyser = audioContextRef.current.createAnalyser();
          const source = audioContextRef.current.createMediaStreamSource(mediaStream);
          source.connect(analyser);
          analyser.fftSize = 256;
          const dataArray = new Uint8Array(analyser.frequencyBinCount);

          const checkAudioLevel = () => {
            if (!isMountedRef.current) return;
            analyser.getByteFrequencyData(dataArray);
            const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
            setAudioLevel(Math.min(100, average * 2));
            animationFrameRef.current = requestAnimationFrame(checkAudioLevel);
          };

          checkAudioLevel();
        } catch (audioErr) {
          console.warn('[useDailyPreJoin] Could not set up audio level monitor:', audioErr);
        }
      }

      setIsReady(true);
      setIsInitializing(false);
      initializingRef.current = false;
      console.log('[useDailyPreJoin] Initialization complete');

    } catch (err) {
      initializingRef.current = false;

      if (!isMountedRef.current) return;

      console.error('[useDailyPreJoin] Error getting media:', err);

      let errorType: PreJoinError['type'] = 'unknown';
      let errorMessage = 'Failed to access camera/microphone';

      if (err instanceof Error) {
        const errorName = err.name;

        if (errorName === 'NotAllowedError' || errorName === 'PermissionDeniedError') {
          errorType = 'permission-denied';
          errorMessage = 'Camera and microphone access was denied. Please allow access in your browser settings and try again.';
        } else if (errorName === 'NotFoundError' || errorName === 'DevicesNotFoundError') {
          errorType = 'no-camera';
          errorMessage = 'No camera or microphone found. Please connect a device and try again.';
        } else if (errorName === 'NotReadableError' || errorName === 'TrackStartError') {
          errorMessage = 'Camera or microphone is already in use by another application.';
        } else {
          errorMessage = err.message;
        }
      }

      setError({ type: errorType, message: errorMessage });
      setIsInitializing(false);
    }
  }, []);

  // Toggle audio - actually mutes/unmutes the track
  const toggleAudio = useCallback(() => {
    if (!stream) return;

    const audioTracks = stream.getAudioTracks();
    const newState = !audioEnabled;

    audioTracks.forEach(track => {
      track.enabled = newState;
    });

    setAudioEnabled(newState);
    console.log('[useDailyPreJoin] Audio toggled to:', newState);
  }, [stream, audioEnabled]);

  // Toggle video - actually enables/disables the video track
  const toggleVideo = useCallback(() => {
    if (!stream) return;

    const videoTracks = stream.getVideoTracks();
    const newState = !videoEnabled;

    videoTracks.forEach(track => {
      track.enabled = newState;
    });

    setVideoEnabled(newState);
    console.log('[useDailyPreJoin] Video toggled to:', newState);
  }, [stream, videoEnabled]);

  // Cleanup function
  const destroy = useCallback(() => {
    console.log('[useDailyPreJoin] Destroying...');

    // Stop animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Close audio context
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }

    // Stop all tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    setStream(null);
  }, []);

  // Retry
  const retry = useCallback(() => {
    console.log('[useDailyPreJoin] Retrying...');
    destroy();
    initializingRef.current = false;
    initialize();
  }, [destroy, initialize]);

  // Initialize on mount
  useEffect(() => {
    isMountedRef.current = true;
    initialize();

    return () => {
      isMountedRef.current = false;
      destroy();
    };
  }, [initialize, destroy]);

  return {
    stream,
    audioEnabled,
    videoEnabled,
    isInitializing,
    isReady,
    error,
    hasCameraDevice,
    hasMicrophoneDevice,
    audioLevel,
    toggleAudio,
    toggleVideo,
    retry,
    destroy,
  };
}

export default useDailyPreJoin;
